import { t } from "i18next";

export type NumberFieldFormat = "currency" | "sqm" | "hours" | "days" | "months" | "years" | "dynamicDate" | "percentage" | "default" | "none";
export type RawNumberFormat = "minutes";

export class NumberUtilities {
  private static convertToMetric = (value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat): number => {
    if (rawDataMetric === "minutes") {
      if (metric === "hours") return Math.round((value / 60) * 10) / 10;
      if (metric === "days") return Math.round((value / 60 / 24) * 10) / 10;
      if (metric === "months") return Math.round((value / 60 / 24 / 30) * 10) / 10;
      if (metric === "years") return Math.round((value / 60 / 24 / 365) * 10) / 10;
    }
    return value;
  };

  static formatValueToString(value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat, maxDecimals?: number): string {
    if (!metric || metric === "default") {
      return value.toLocaleString("no-NO", { maximumFractionDigits: maxDecimals });
    }

    if (metric === "currency") {
      return value.toLocaleString("no-NO", { maximumFractionDigits: maxDecimals }) + " NOK";
    }
    if (metric === "sqm") {
      return value.toLocaleString("no-NO", { maximumFractionDigits: maxDecimals }) + " m²";
    }
    if (metric === "percentage") {
      const percentage = value * 100;
      return (Math.round(percentage * 10) / 10).toLocaleString("no-NO") + " %";
    }
    if (metric === "hours") {
      return this.formatHours(value, metric, rawDataMetric);
    }
    if (metric === "days") {
      return this.formatDays(value, metric, rawDataMetric);
    }
    if (metric === "months") {
      return this.formatMonths(value, metric, rawDataMetric);
    }
    if (metric === "years") {
      return this.formatYears(value, metric, rawDataMetric);
    }
    if (metric === "dynamicDate") {
      const numberOfDays = this.convertToMetric(value, "days", rawDataMetric);
      if (numberOfDays < 1) return this.formatHours(value, "hours", rawDataMetric);
      if (numberOfDays < 30) return this.formatDays(value, "days", rawDataMetric);
      if (numberOfDays < 365) return this.formatMonths(value, "months", rawDataMetric);
      return this.formatYears(value, "years", rawDataMetric);
    }

    return "" + value;
  }

  private static formatHours = (value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat): string => {
    const numberOfHours = this.convertToMetric(value, metric, rawDataMetric);
    return numberOfHours.toLocaleString("no-NO") + " " + (numberOfHours > 1 ? t("main.hours") : t("main.hour")).toLowerCase();
  };

  private static formatDays = (value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat): string => {
    const numberOfDays = this.convertToMetric(value, metric, rawDataMetric);
    return numberOfDays.toLocaleString("no-NO") + " " + (numberOfDays > 1 ? t("main.days") : t("main.day")).toLowerCase();
  };

  private static formatMonths = (value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat): string => {
    const numberOfMonths = this.convertToMetric(value, metric, rawDataMetric);
    return numberOfMonths.toLocaleString("no-NO") + " " + (numberOfMonths > 1 ? t("main.months") : t("main.month")).toLowerCase();
  };

  private static formatYears = (value: number, metric: NumberFieldFormat | undefined, rawDataMetric?: RawNumberFormat): string => {
    const numberOfYears = this.convertToMetric(value, metric, rawDataMetric);
    return numberOfYears.toLocaleString("no-NO") + " " + (numberOfYears > 1 ? t("main.years") : t("main.year")).toLowerCase();
  };
}
